/**
 * Ness Frame Character 이미지 체커 (Frontend 전용)
 * Canvas 기반 이미지 분석 유틸리티
 */
// @ts-expect-error - JS 모듈 (타입 선언 없음)
import { createCanvasElement, loadImageFromUrl } from '@modules/Canvas/S/util';
import { CharacterValidatorS } from '@modules/NessLabs/S/Function/character';
import { ConfigSentryF } from '@modules/Config/Sentry/F/Function/module/ConfigSentryF';

// ============================================
// Constants
// ============================================
const CHARACTER_CANVAS_CONFIG = {
  crop_size: { w: 300, h: 188 },
  crop_start_position: { x: 0, y: 0 },
  move_position: { x: 0, y: 0 },
  checker: {
    warning: { widthMm: 68, heightMm: 91 },
    impossible: { widthMm: 280, heightMm: 154 },
  },
} as const;

/**
 * 픽셀 가시성 판단 설정
 * - ALPHA_VISIBILITY_THRESHOLD: 사람 눈에 인지 가능한 최소 alpha 값 (0-255)
 *   50 = alpha 50, 이 값 이상이면 어느 정도 뚜렷하게 보이는 수준
 * - MIN_SIGNIFICANT_PIXEL_COUNT: 유의미하다고 판단할 최소 픽셀 개수
 */
export const PIXEL_VISIBILITY_CONFIG = {
  ALPHA_VISIBILITY_THRESHOLD: 50,
  MIN_SIGNIFICANT_PIXEL_COUNT: 10,
} as const;

// ============================================
// Types
// ============================================
interface AnalyzeInput {
  image_url: string;
  avatar_items: object[];
  special_world_character_flag: boolean;
}

interface AnalyzeOutput {
  warning?: {
    width: boolean;
    height: boolean;
    width_diff: number;
    height_diff: number;
    has_bottom_overflow: boolean;
  };
  impossible?: { width?: boolean; height?: boolean; is_collabo_character_or_special_world?: boolean };
}

// ============================================
// Helper Functions
// ============================================

/**
 * URL에서 캔버스 생성
 */
async function makeCanvasByUrl(url: string): Promise<HTMLCanvasElement> {
  const img = await loadImageFromUrl(url);
  const canvas = createCanvasElement({ width: img.width, height: img.height });
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0);
  return canvas;
}

/**
 * 캔버스 크롭
 */
function cropCanvas(
  canvas: HTMLCanvasElement,
  bounds: { x: number; y: number; width: number; height: number },
): HTMLCanvasElement {
  const croppedCanvas = createCanvasElement({ width: bounds.width, height: bounds.height });
  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(canvas, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
  return croppedCanvas;
}

function getImageData({ canvas }: { canvas: HTMLCanvasElement }) {
  const ctx = canvas.getContext('2d');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return ctx!.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * Convert mm to px at 300 DPI
 */
function mmToPx(mm: number): number {
  return mm * 1.09099240306;
}

// ============================================
// Exported Functions
// ============================================

/**
 * 이미지의 불투명 영역 바운딩 박스 계산
 */
function getJustifyImageSize({ canvas, margin = 0 }: { canvas: HTMLCanvasElement; margin?: number }) {
  const imgData = getImageData({ canvas });

  const pointer: { top: number | null; bottom: number | null; left: number | null; right: number | null } = {
    top: null,
    bottom: null,
    left: null,
    right: null,
  };
  const { width: w, height: h } = imgData;

  const getAlpha = (i: number, j: number) => imgData.data[(i * w + j) * 4 + 3] ?? 0;
  const alpha_threshold = 1;

  for (let i = 0; i < h; ++i) {
    if (pointer.top != null && pointer.bottom != null) break;
    for (let j = 0; j < w; ++j) {
      pointer.top == null && getAlpha(i, j) > alpha_threshold && (pointer.top = i);
      pointer.bottom == null && getAlpha(h - i - 1, j) > alpha_threshold && (pointer.bottom = h - i - 1);
    }
  }

  for (let j = 0; j < w; ++j) {
    if (pointer.left != null && pointer.right != null) break;
    for (let i = (pointer.top ?? 0) + 1; i < (pointer.bottom ?? 0) - 1; ++i) {
      pointer.left == null && getAlpha(i, j) > alpha_threshold && (pointer.left = j);
      pointer.right == null && getAlpha(i, w - j - 1) > alpha_threshold && (pointer.right = w - j - 1);
    }
  }

  return {
    x: (pointer.left ?? 0) - margin,
    y: (pointer.top ?? 0) - margin,
    w: (pointer.right ?? 0) - (pointer.left ?? 0) + 1 + margin * 2,
    h: (pointer.bottom ?? 0) - (pointer.top ?? 0) + 1 + margin * 2,
  };
}

/**
 * 단일 픽셀이 사람 눈에 인지 가능한 수준인지 판단
 */
export function isPixelVisible(alpha: number): boolean {
  return alpha >= PIXEL_VISIBILITY_CONFIG.ALPHA_VISIBILITY_THRESHOLD;
}

/**
 * ImageData에서 인지 가능한 픽셀 개수를 카운트
 */
export function countVisiblePixels(img_data: ImageData): number {
  let count = 0;
  for (let i = 0; i < img_data.data.length; i += 4) {
    const alpha = img_data.data[i + 3] ?? 0;
    if (isPixelVisible(alpha)) {
      count++;
    }
  }
  return count;
}

/**
 * 인지 가능한 픽셀이 유의미한 양만큼 존재하는지 판단
 */
export function hasSignificantVisiblePixels(img_data: ImageData): boolean {
  const visible_count = countVisiblePixels(img_data);
  return visible_count >= PIXEL_VISIBILITY_CONFIG.MIN_SIGNIFICANT_PIXEL_COUNT;
}

/**
 * crop 영역 하단 아래에 유의미한 양의 불투명 픽셀이 존재하는지 체크
 *
 * 단순히 alpha > 0인 픽셀이 하나라도 있는지가 아니라,
 * 사람 눈에 인지 가능한 수준(alpha >= 80)의 픽셀이
 * 일정 개수(10개) 이상 존재하는지를 판단합니다.
 */
export function hasOpaquePixelsBelowCropArea({
  canvas,
  crop_bottom_y,
  check_height = 1,
}: {
  canvas: HTMLCanvasElement;
  crop_bottom_y: number;
  check_height?: number;
}): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const { width, height } = canvas;
  const start_y = crop_bottom_y;
  const end_y = Math.min(crop_bottom_y + check_height, height);

  if (start_y >= height) return false;

  const img_data = ctx.getImageData(0, start_y, width, end_y - start_y);

  return hasSignificantVisiblePixels(img_data);
}

/**
 * 캐릭터 이미지의 불투명 영역 분석 및 warning/impossible 상태 판정
 */
export async function checkCharacterImageOpaqueArea(input: AnalyzeInput): Promise<AnalyzeOutput> {
  try {
    const { image_url, avatar_items, special_world_character_flag } = input;
    const { warning, impossible } = CHARACTER_CANVAS_CONFIG.checker;
    const { crop_start_position, move_position, crop_size } = CHARACTER_CANVAS_CONFIG;
    const is_collabo_character_or_special_world = CharacterValidatorS.isCollaboCharacter({
      avatar_items,
      special_world_character_flag,
    });
    if (is_collabo_character_or_special_world) {
      return {
        impossible: {
          is_collabo_character_or_special_world: true,
        },
      };
    }

    const original_canvas = await makeCanvasByUrl(image_url);

    const canvas = cropCanvas(original_canvas, {
      x: crop_start_position.x + move_position.x,
      y: crop_start_position.y + move_position.y,
      width: crop_size.w,
      height: crop_size.h,
    });

    const size = getJustifyImageSize({ canvas, margin: 0 });
    const { w: widthPx, h: heightPx } = size;

    const warningPx = mmToPx(warning.widthMm);
    const warningPxHeight = mmToPx(warning.heightMm);
    const impossiblePx = mmToPx(impossible.widthMm);
    const impossiblePxHeight = mmToPx(impossible.heightMm);

    // crop 영역 하단 아래에 이미지가 있는지 체크
    const crop_bottom_y = crop_start_position.y + move_position.y + crop_size.h;
    const has_bottom_overflow = hasOpaquePixelsBelowCropArea({
      canvas: original_canvas,
      crop_bottom_y,
      check_height: 1,
    });
    return {
      warning: {
        width: widthPx >= warningPx,
        width_diff: widthPx - warningPx,
        height: heightPx >= warningPxHeight,
        height_diff: heightPx - warningPxHeight,
        has_bottom_overflow,
      },
      impossible: {
        width: widthPx >= impossiblePx,
        height: heightPx >= impossiblePxHeight,
        is_collabo_character_or_special_world,
      },
    };
  } catch (e) {
    ConfigSentryF.criticalError(e);
    return {
      impossible: {
        is_collabo_character_or_special_world: true,
      },
    };
  }
}
